import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { AuditLogFilterField, AuditLogFilterTag, AuditLogFilterOperator } from "@/hooks/useAdminAuditLogs";

interface AuditLogColumnFiltersProps {
  filterTags: AuditLogFilterTag[];
  addFilter: (field: AuditLogFilterField, value: string, operator?: AuditLogFilterOperator) => void;
  removeFilter: (tagId: string) => void;
  clearAllFilters: () => void;
}

const FIELD_OPTIONS: {
  value: AuditLogFilterField;
  label: string;
  placeholder: string;
  options?: { value: string; label: string }[];
  operators?: AuditLogFilterOperator[];
}[] = [
  {
    value: "action",
    label: "Action",
    placeholder: "Select action type",
    options: [
      { value: "update", label: "Update" },
      { value: "delete", label: "Delete" },
    ],
  },
  { value: "admin", label: "Admin", placeholder: "Search by admin name...", operators: ["contains", "equals"] },
  { value: "target", label: "Target User", placeholder: "Search by target user...", operators: ["contains", "equals"] },
];

const FIELD_COLORS: Record<AuditLogFilterField, string> = {
  action: "bg-orange-500/20 text-orange-700 border-orange-500/30",
  admin: "bg-blue-500/20 text-blue-700 border-blue-500/30",
  target: "bg-purple-500/20 text-purple-700 border-purple-500/30",
};

const OPERATOR_LABELS: Record<AuditLogFilterOperator, string> = {
  contains: "contains",
  equals: "=",
};

export const AuditLogColumnFilters = ({
  filterTags,
  addFilter,
  removeFilter,
  clearAllFilters,
}: AuditLogColumnFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<AuditLogFilterField>("action");
  const [selectedOperator, setSelectedOperator] = useState<AuditLogFilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");

  const selectedFieldConfig = FIELD_OPTIONS.find((f) => f.value === selectedField);
  const hasOptions = !!selectedFieldConfig?.options;
  const hasOperators = !!selectedFieldConfig?.operators && selectedFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (inputValue.trim()) {
      const operator = hasOperators ? selectedOperator : undefined;
      addFilter(selectedField, inputValue.trim(), operator);
      setInputValue("");
      setIsOpen(false);
    }
  };

  const handleSelectOption = (value: string) => {
    addFilter(selectedField, value);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: AuditLogFilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: AuditLogFilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              {hasOptions ? (
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.options?.map((option) => (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleSelectOption(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={selectedFieldConfig?.placeholder || "Enter value..."}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                  />
                  <Button size="sm" className="h-8" onClick={handleAddFilter}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {filterTags.map((tag) => (
        <div
          key={tag.id}
          className={cn(
            "flex items-center gap-1 h-7 px-2 rounded-md border text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.label}:</span>
          <span>{formatTagDisplay(tag)}</span>
          <button
            onClick={() => removeFilter(tag.id)}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Clear all
        </Button>
      )}
    </div>
  );
};
